/**
 * VIEW MENU — منوی اصلی رستوران
 */

import { formatPrice, escapeHtml, ImageCache } from '../utils.js';

export function renderLoading() {
  document.getElementById('menu-sections-container').innerHTML = `
    <div class="loading-state" style="text-align:center;padding:60px 20px;">
      <i class="fa-solid fa-spinner fa-spin" style="font-size:2rem;color:var(--primary);margin-bottom:12px;"></i>
      <p style="color:var(--text-muted);font-size:0.9rem;">در حال بارگذاری منو...</p>
    </div>`;
  document.getElementById('category-tabs-container').innerHTML = '';
}

export function renderError(message, onRetry) {
  document.getElementById('menu-sections-container').innerHTML = `
    <div class="loading-state" style="text-align:center;padding:60px 20px;">
      <i class="fa-solid fa-triangle-exclamation" style="font-size:2rem;color:var(--primary);margin-bottom:12px;"></i>
      <p style="color:var(--text-muted);font-size:0.9rem;margin-bottom:16px;">${escapeHtml(message)}</p>
      <button id="btn-menu-retry" class="action-button" style="margin:0 auto;">
        <i class="fa-solid fa-rotate-right"></i> <span>تلاش مجدد</span>
      </button>
    </div>`;
  document.getElementById('btn-menu-retry')?.addEventListener('click', onRetry);
}

export function renderRestaurantStatus(config) {
  const badge = document.getElementById('restaurant-status-badge');
  if (!badge) return;
  badge.innerHTML = config.isOpen
    ? `<span class="status-badge"><i class="fa-solid fa-circle"></i> رستوران باز است</span>`
    : `<span class="status-badge closed"><i class="fa-solid fa-circle-xmark"></i> بسته (ساعت کار: ${escapeHtml(config.openTime)})</span>`;
}

export async function buildStructure(categories, products, callbacks) {
  _buildTabs(categories, callbacks.onTabClick);
  _buildSections(categories, products, callbacks);

  // Preload all product images into cache in the background
  const urls = products.map(p => p.image).filter(Boolean);
  ImageCache.preload(urls);

  // Fix sticky category bar: ensure parent has correct positioning
  const tabsWrapper = document.querySelector('.category-tabs-container');
  if (tabsWrapper) {
    tabsWrapper.style.position = 'sticky';
    tabsWrapper.style.top      = '0';
    tabsWrapper.style.zIndex   = '100';
  }
}

function _buildTabs(categories, onTabClick) {
  const container = document.getElementById('category-tabs-container');
  if (!container) return;
  container.innerHTML = '';
  categories.forEach((cat, idx) => {
    const tab = document.createElement('div');
    tab.className   = `category-tab ${idx === 0 ? 'active' : ''}`;
    tab.id          = `tab-${cat.id}`;
    tab.textContent = cat.name;
    tab.addEventListener('click', () => onTabClick(cat.id));
    container.appendChild(tab);
  });
}

function _buildSections(categories, products, callbacks) {
  const container = document.getElementById('menu-sections-container');
  if (!container) return;
  container.innerHTML = '';
  categories.forEach(cat => {
    const catProducts = products.filter(p => p.category === cat.id);
    if (catProducts.length === 0) return;
    const section = document.createElement('section');
    section.id        = `section-${cat.id}`;
    section.className = 'menu-category-section';
    const title = document.createElement('h2');
    title.className   = 'section-title';
    title.textContent = cat.name;
    section.appendChild(title);
    catProducts.forEach(prod => section.appendChild(_createCard(prod, callbacks)));
    container.appendChild(section);
  });
}

function _createCard(prod) {
  const wrapper       = document.createElement('div');
  wrapper.className   = 'product-card-container';
  wrapper.id          = `card-container-${prod.id}`;

  let badge = prod.badge ? `<span class="badge product-image-badge">${escapeHtml(prod.badge)}</span>` : '';
  if (!prod.available) badge = `<span class="badge badge-out product-image-badge">ناموجود</span>`;

  // Use cached image if available, fall back to network
  const cached = ImageCache.get(prod.image);
  const imgSrc = cached || prod.image || `https://placehold.co/150x150/ff5c35/ffffff?text=${encodeURIComponent(prod.name)}`;

  const card = document.createElement('div');
  card.className = `product-card ${!prod.available ? 'unavailable' : ''}`;
  card.innerHTML = `
    <div class="product-image-side">
      <img src="${escapeHtml(imgSrc)}" alt="${escapeHtml(prod.name)}">
      ${badge}
    </div>
    <div class="product-content-side">
      <div>
        <h3 class="product-name">${escapeHtml(prod.name)}</h3>
        <p class="product-desc">${escapeHtml(prod.description)}</p>
      </div>
      <div class="product-footer">
        <div class="price-tag">${formatPrice(prod.price)}</div>
        <div class="action-zone" id="action-zone-${prod.id}"></div>
      </div>
    </div>`;
  wrapper.appendChild(card);

  const expanded     = document.createElement('div');
  expanded.className = 'expanded-configs hidden';
  expanded.id        = `expanded-configs-${prod.id}`;
  wrapper.appendChild(expanded);

  return wrapper;
}

export function updateProductCard(prod, totalQty, cartItems, isOpen, callbacks) {
  const actionZone  = document.getElementById(`action-zone-${prod.id}`);
  const configsZone = document.getElementById(`expanded-configs-${prod.id}`);
  if (!actionZone || !configsZone) return;

  // اگر محصول هیچ افزودنی‌ای ندارد
if (!prod.afzoodanis || prod.afzoodanis.length === 0) {
  // مخفی‌سازی expanded-configs و خالی کردن آن
  configsZone.classList.add('hidden');
  configsZone.innerHTML = '';

  // نمایش دکمهٔ عادی در action-zone (مثل حالت مودال ولی بدون باز شدن مودال)
  actionZone.innerHTML = '';

  if (!isOpen) {
    actionZone.innerHTML = `<button class="action-button" disabled style="font-size:.75rem;">بسته است</button>`;
  } else if (!prod.available) {
    actionZone.innerHTML = `<button class="action-button" disabled style="font-size:.75rem;">ناموجود</button>`;
  } else if (totalQty === 0) {
    const btn = document.createElement('button');
    btn.className = 'action-button';
    btn.innerHTML = `<i class="fa-solid fa-plus"></i> <span>افزودن</span>`;
    btn.addEventListener('click', e => { e.stopPropagation(); callbacks.onAddClick(prod); });
    actionZone.appendChild(btn);
  } else {
    const ctrl = document.createElement('div');
    ctrl.className = 'quantity-control';
    ctrl.innerHTML = `
      <button class="btn-qty btn-minus"><i class="fa-solid fa-minus"></i></button>
      <span class="qty-val">${totalQty}</span>
      <button class="btn-qty btn-plus"><i class="fa-solid fa-plus"></i></button>`;
    ctrl.querySelector('.btn-plus').addEventListener('click',  e => { e.stopPropagation(); callbacks.onAddClick(prod); });
    ctrl.querySelector('.btn-minus').addEventListener('click', e => { e.stopPropagation(); callbacks.onRemoveClick(prod.id); });
    actionZone.appendChild(ctrl);
  }
  return; // کار تمام است و دیگر ادامه نمی‌دهیم
}

  // فقط محصولاتی که دقیقاً true باشند مودال می‌شوند
  const useModal = prod.showAfzoodanisInModal === true;

  if (!useModal) {
    // ── حالت مستقیم: دکمۀ اصلی حذف، افزودنی‌ها زیر کارت ──
    actionZone.innerHTML = '';                     // دکمۀ "افزودن" کارت حذف شود
    configsZone.classList.remove('hidden');        // همیشه نمایش
    configsZone.innerHTML = '';

    if (!isOpen) {
      configsZone.innerHTML = `<div style="text-align:center;padding:8px;"><button class="action-button" disabled>بسته است</button></div>`;
      return;
    }
    if (!prod.available) {
      configsZone.innerHTML = `<div style="text-align:center;padding:8px;"><button class="action-button" disabled>ناموجود</button></div>`;
      return;
    }

    // گزینه‌ها: ساده + هر افزودنی (هر کدام یک ردیف مجزا)
    const options = [];
    options.push({ label: 'ساده', selectedAddons: [], priceDelta: 0 });
    (prod.afzoodanis || []).forEach((addon, idx) => {
      const delta = (prod.gheymat_afzoodanis || [])[idx] || 0;
      options.push({ label: addon, selectedAddons: [addon], priceDelta: delta });
    });

    options.forEach(opt => {
      const linePrice = prod.price + opt.priceDelta;
      const existing = cartItems.find(item =>
        item.selectedAddons.length === opt.selectedAddons.length &&
        item.selectedAddons.every((a, i) => a === opt.selectedAddons[i])
      );

      const row = document.createElement('div');
      row.className = 'config-row';

      if (existing) {
        row.innerHTML = `
          <div class="config-details">
            <span class="config-addons">${escapeHtml(opt.label)}</span>
            <span class="text-muted" style="font-size:.68rem;">قیمت واحد: ${formatPrice(linePrice)}</span>
          </div>
          <div class="config-right">
            <span class="config-price">${formatPrice(linePrice * existing.quantity)}</span>
            <div class="quantity-control" style="transform:scale(.95);">
              <button class="btn-qty btn-sub-minus"><i class="fa-solid fa-minus"></i></button>
              <span class="qty-val">${existing.quantity}</span>
              <button class="btn-qty btn-sub-plus"><i class="fa-solid fa-plus"></i></button>
            </div>
          </div>`;
        row.querySelector('.btn-sub-plus').addEventListener('click', e => {
          e.stopPropagation();
          callbacks.onConfigAdd(prod.id, opt.selectedAddons);
        });
        row.querySelector('.btn-sub-minus').addEventListener('click', e => {
          e.stopPropagation();
          callbacks.onConfigRemove(prod.id, opt.selectedAddons);
        });
      } else {
        row.innerHTML = `
          <div class="config-details">
            <span class="config-addons">${escapeHtml(opt.label)}</span>
            <span class="text-muted" style="font-size:.68rem;">${opt.priceDelta >= 0 ? '+' + formatPrice(opt.priceDelta) : formatPrice(opt.priceDelta)}</span>
          </div>
          <div class="config-right">
            <button class="action-button" style="font-size:.75rem; padding:4px 12px;">
              <i class="fa-solid fa-plus"></i> <span>افزودن</span>
            </button>
          </div>`;
        row.querySelector('.action-button').addEventListener('click', e => {
          e.stopPropagation();
          callbacks.onConfigAdd(prod.id, opt.selectedAddons);
        });
      }
      configsZone.appendChild(row);
    });
    return;
  }

  // ── حالت مودال (showAfzoodanisInModal === true) ──
  actionZone.innerHTML = '';

  if (!isOpen) {
    actionZone.innerHTML = `<button class="action-button" disabled style="font-size:.75rem;">بسته است</button>`;
  } else if (!prod.available) {
    actionZone.innerHTML = `<button class="action-button" disabled style="font-size:.75rem;">ناموجود</button>`;
  } else if (totalQty === 0) {
    const btn = document.createElement('button');
    btn.className = 'action-button';
    btn.innerHTML = `<i class="fa-solid fa-plus"></i> <span>افزودن</span>`;
    btn.addEventListener('click', e => { e.stopPropagation(); callbacks.onAddClick(prod); });
    actionZone.appendChild(btn);
  } else {
    const ctrl = document.createElement('div');
    ctrl.className = 'quantity-control';
    ctrl.innerHTML = `
      <button class="btn-qty btn-minus"><i class="fa-solid fa-minus"></i></button>
      <span class="qty-val">${totalQty}</span>
      <button class="btn-qty btn-plus"><i class="fa-solid fa-plus"></i></button>`;
    ctrl.querySelector('.btn-plus').addEventListener('click',  e => { e.stopPropagation(); callbacks.onAddClick(prod); });
    ctrl.querySelector('.btn-minus').addEventListener('click', e => { e.stopPropagation(); callbacks.onRemoveClick(prod.id); });
    actionZone.appendChild(ctrl);
  }

  _updateConfigRows(configsZone, prod, totalQty, cartItems, callbacks);
}

function _updateConfigRows(container, prod, totalQty, cartItems, callbacks) {
  if (!container) return;
  if (totalQty === 0 || cartItems.length === 0) {
    container.innerHTML = '';
    container.classList.add('hidden');
    return;
  }
  container.innerHTML = '';
  container.classList.remove('hidden');
  cartItems.forEach(config => {
    const summary = config.selectedAddons.length > 0
      ? config.selectedAddons.join(' + ') : 'ساده (بدون افزودنی)';
    const row = document.createElement('div');
    row.className = 'config-row';
    row.innerHTML = `
      <div class="config-details">
        <span class="config-addons">${escapeHtml(summary)}</span>
        <span class="text-muted" style="font-size:.68rem;">قیمت واحد: ${formatPrice(config.linePrice)}</span>
      </div>
      <div class="config-right">
        <span class="config-price">${formatPrice(config.linePrice * config.quantity)}</span>
        <div class="quantity-control" style="transform:scale(.95);">
          <button class="btn-qty btn-sub-minus"><i class="fa-solid fa-minus"></i></button>
          <span class="qty-val">${config.quantity}</span>
          <button class="btn-qty btn-sub-plus"><i class="fa-solid fa-plus"></i></button>
        </div>
      </div>`;
    row.querySelector('.btn-sub-plus').addEventListener('click',  e => { e.stopPropagation(); callbacks.onConfigAdd(prod.id, config.selectedAddons); });
    row.querySelector('.btn-sub-minus').addEventListener('click', e => { e.stopPropagation(); callbacks.onConfigRemove(prod.id, config.selectedAddons); });
    container.appendChild(row);
  });
}

export function updateActiveTab(categoryId) {
  document.querySelectorAll('.category-tab').forEach(t => t.classList.remove('active'));
  const tab = document.getElementById(`tab-${categoryId}`);
  if (tab) {
    tab.classList.add('active');
    // Keep active tab visible inside the scrollable tab bar
    // tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }
}

export function scrollToCategory(categoryId) {
  document.getElementById(`section-${categoryId}`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function updateFloatingCart(totalCount, totalAmount) {
  const bar = document.getElementById('floating-cart');
  if (!bar) return;
  if (totalCount > 0) {
    bar.classList.remove('hidden');
    document.getElementById('floating-cart-qty').textContent   = totalCount.toLocaleString('fa-IR');
    document.getElementById('floating-cart-total').textContent = formatPrice(totalAmount);
  } else {
    bar.classList.add('hidden');
  }
}

// ─── Addon Sheet ──────────────────────────────────────────────────────────────

export function openAddonSheet(product, onPriceChange) {
  document.getElementById('sheet-addon-title').textContent     = 'انتخاب افزودنی‌ها';
  document.getElementById('sheet-addon-subtitle').textContent  = product.name;
  document.getElementById('sheet-addons-total-price').textContent = formatPrice(product.price);

  const list = document.getElementById('addons-list-container');
  list.innerHTML = '';

  (product.afzoodanis || []).forEach((addon, idx) => {
    const delta     = (product.gheymat_afzoodanis || [])[idx] || 0;
    const deltaText = delta >= 0 ? `+${formatPrice(delta)}` : formatPrice(delta);
    const row = document.createElement('div');
    row.className = 'addon-row';
    row.innerHTML = `
      <div class="addon-label-side">
        <input type="checkbox" class="addon-checkbox" id="chk-addon-${idx}" value="${escapeHtml(addon)}">
        <label for="chk-addon-${idx}">${escapeHtml(addon)}</label>
      </div>
      <div class="addon-price ${delta >= 0 ? 'text-secondary' : 'text-primary'}">${deltaText}</div>`;
    const refresh = () => onPriceChange(getSelectedAddons());
    row.addEventListener('click', e => {
      if (e.target.tagName !== 'INPUT') {
        row.querySelector('.addon-checkbox').checked = !row.querySelector('.addon-checkbox').checked;
        refresh();
      }
    });
    row.querySelector('.addon-checkbox').addEventListener('change', refresh);
    list.appendChild(row);
  });

  document.getElementById('bottom-sheet-backdrop').classList.add('active');
  document.getElementById('addon-bottom-sheet').classList.add('active');
}

export function closeAddonSheet() {
  document.getElementById('bottom-sheet-backdrop').classList.remove('active');
  document.getElementById('addon-bottom-sheet').classList.remove('active');
}

export function getSelectedAddons() {
  return Array.from(document.querySelectorAll('.addon-checkbox:checked')).map(c => c.value);
}

export function updateSheetTotalPrice(amount) {
  document.getElementById('sheet-addons-total-price').textContent = formatPrice(amount);
}
